import { Inject, Injectable } from '@angular/core'
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import {
    GeneralMapLayerDefinitionsService
} from '../../domain/general-map/components/general-map/services/general-map-layer-definitions.service'
import { AuthContextStorageService } from '../../core/auth/auth-context-storage.service'
import { OpenLayersService } from './open-layers.service'
import VectorSource from 'ol/source/Vector'
import _ from 'lodash'
import { MapLpisLayerService } from '../../features/map/services/map-lpis-layer.service'

@Injectable({
    providedIn: 'root',
})
export class OpenLayersLayerService {

    constructor(
        @Inject(MapLpisLayerService) private mapLpisLayerService: MapLpisLayerService,
        @Inject(GeneralMapLayerDefinitionsService) private generalMapLayerDefinitionsService: GeneralMapLayerDefinitionsService,
        @Inject(AuthContextStorageService) private authContextStorageService: AuthContextStorageService,
        @Inject(OpenLayersService) private openLayersService: OpenLayersService,
    ) {
    }

    /**
     * Přidá featury do daného zdroje a doplní jim `geometryOrder` a id.
     *
     * DEV-2101: dřív si metoda vrstvu dohledávala sama podle jména na mapě — a když ji
     * nenašla, tiše neudělala nic. To dohledání patří do `GeneralMapLayerService`, který
     * jako jediný vidí i registr `featureGroupLayers$`; `shared/` o něm vědět nemá.
     * Tím též sedí se sousedním `OpenLayersGeoJsonService`, jehož celé API bere zdroj.
     */
    public addFeaturesToVectorSource(features, vectorSource: VectorSource<any>): void {
        if (!vectorSource) {
            return
        }

        let maxOrder = _.max(_.map(vectorSource.getFeatures(), f => f.get('geometryOrder'))) || 0
        features.forEach(feature => {
            if (_.isNil(feature.get('geometryOrder'))) {
                feature.set('geometryOrder', ++maxOrder)
            }
            feature.setId('a' + (Math.floor(Math.random() * 1000000000)).toString())
        })
        vectorSource.addFeatures(features)
    }

    public buildLayersFromConfig(config: any): any {
        const layers = []

        if (config?.layers?.length) {
            config.layers.forEach(layerConfig => {
                if (layerConfig.layers) {
                    const underLayers = this.buildLayersFromConfig(layerConfig)

                    if (underLayers.length > 0) {
                        underLayers.forEach(underLayer => layers.push(underLayer))
                    }
                } else {
                    const layer = this.buildLayerFromConfig(layerConfig, layerConfig.visible)

                    if (layer) {
                        layers.push(layer)
                    }
                }
            })
        }

        return layers
    }

    public buildLayerFromConfig(layerConfig: any, visibility: boolean): TileLayer<any> {
        let layerDefinition

        if (_.startsWith(layerConfig.id, 'lpis_user_')) {
            layerDefinition = this.mapLpisLayerService.generateMapLpisLayer(layerConfig.color, layerConfig.lpisId)
        } else {
            layerDefinition = this.generalMapLayerDefinitionsService.getLayerById(
                layerConfig.id,
                this.authContextStorageService.getProjectKey())
        }

        if (layerConfig.filter) {
            layerDefinition.layer.filter = layerConfig.filter
        }

        return this.buildLayerFromDefinition(layerDefinition, visibility)
    }

    public buildLayerFromDefinition(layerDefinition: any, visibility: boolean): TileLayer<any> {
        if (layerDefinition && layerDefinition.layer && layerDefinition.layer.type) {
            switch (layerDefinition.layer.type) {
                case 'wms':
                case 'wmts':
                    return new TileLayer(this.openLayersService.transformLeafletConfigToOpenLayers(layerDefinition, visibility))
            }
        }

        return null
    }

    public createVectorLayer(layerName: string, vectorSource: any, zIndex: number): VectorLayer<any> {
        return new VectorLayer({
            properties: {
                name: layerName
            },
            source: vectorSource,
            zIndex: zIndex,
        })
    }
}